'use strict';

var addressConstants = require('./constants');

/**
 * Marks an input as managed by the shared address validation engine.
 * Stored via jQuery data so no markup is added or removed.
 */
var FIELD_MARKER = 'fgAddressValidationField';
var BRIDGE_MARKER = 'fgAddressValidationBridge';

/**
 * Built-in jquery-validation rules that can be auto-derived from HTML
 * attributes rendered by the DWForm templates (required/pattern/maxlength/
 * minlength/number...). They are neutralized so the ONLY rules in play are
 * fgRequired/fgPattern, which reuse the project's existing data-* messages.
 */
var NEUTRALIZED_BUILTIN_RULES = {
    required: false,
    pattern: false,
    maxlength: false,
    minlength: false,
    rangelength: false,
    range: false,
    min: false,
    max: false,
    number: false,
    digits: false,
    email: false,
    url: false,
    date: false,
    dateISO: false,
    time: false
};

var engineMethodsRegistered = false;

/**
 * Registers the custom jquery-validation methods used to reproduce the
 * exact semantics of validateFieldInteractive():
 *  - fgRequired: trims the value before the empty check (like today).
 *  - fgPattern: an empty value is always valid; non-empty must match the regex.
 * @returns {void}
 */
function registerEngineMethods() {
    if (engineMethodsRegistered) {
        return;
    }

    $.validator.addMethod('fgRequired', function (value, element, param) {
        if (!param) {
            return true;
        }
        var val = value === undefined || value === null ? '' : String(value);
        return val.trim() !== '';
    }, undefined);

    $.validator.addMethod('fgPattern', function (value, element, param) {
        var val = value === undefined || value === null ? '' : String(value);
        if (val.trim() === '') {
            return true;
        }
        return param.test(val);
    }, undefined);

    engineMethodsRegistered = true;
}

/**
 * Value normalizer for the phone field: jquery-validation rules see the
 * national 10-digit value (Matriz rule = exactly 10 digits). The visible
 * controlledPhoneInput display carries the "+52 " country prefix, so the
 * leading 52 is dropped when the display is in +52 format. The stored and
 * submitted payload (_phone hidden input) stays untouched.
 *
 * @param {string} value - Raw input value (display format or raw digits).
 * @returns {string} National digits-only value.
 */
function digitsOnly(value) {
    var val = value === undefined || value === null ? '' : String(value);
    var digits = val.replace(/\D/g, '');
    if (/^\+52/.test(val)) {
        digits = digits.replace(/^52/, '');
    }
    return digits;
}

/**
 * Resolves the empty/pattern/minlength messages for a field, preserving
 * today's precedence: data-* attributes from the template first, then JS
 * fallbacks. No new literals: the attributes are sourced from properties by
 * the templates (current architecture).
 *
 * @param {jQuery} $field - Managed input element.
 * @param {string} fieldName - Logical field name (e.g. 'externalNumber').
 * @returns {Object} Messages map for the jquery-validation rules.
 */
function resolveFieldMessages($field, fieldName) {
    var fallbacks = addressConstants.FIELD_MESSAGE_FALLBACKS;
    var messages = {
        fgRequired: $field.data('missing-error') || fallbacks.empty,
        fgPattern: $field.data('pattern-mismatch')
            || fallbacks.patternByField[fieldName]
            || fallbacks.pattern
    };

    if ($field.data('minlength-error')) {
        messages.minlength = $field.data('minlength-error');
    }

    return messages;
}

/**
 * Registers validator rules/messages for every name variant a field may
 * carry over time. controlledPhoneInput renames tel inputs to
 * '{name}Display' (and inserts a hidden '{name}' input) either before or
 * after the engine is initialized, so rules must resolve under both names.
 *
 * @param {Object} validator - The jquery-validation instance.
 * @param {string} name - Current element name.
 * @param {Object} rules - Rule map to merge.
 * @param {Object|null} messages - Message map to merge (or null).
 * @returns {void}
 */
function mirrorValidatorRule(validator, name, rules, messages) {
    if (!name) {
        return;
    }

    var settings = validator.settings;
    var baseName = name.replace(/Display$/, '');
    var names = [name, baseName, baseName + 'Display'];

    names.forEach(function (targetName) {
        if (!targetName) {
            return;
        }
        settings.rules[targetName] = $.extend(
            true, {}, settings.rules[targetName] || {}, rules);
        if (messages) {
            settings.messages[targetName] = $.extend(
                true, {}, settings.messages[targetName] || {}, messages);
        }
    });
}

/**
 * Attaches per-context rules to the managed address fields of a form and
 * neutralizes attribute-derived rules (required/maxlength/minlength coming
 * from the DWForm-rendered markup) on every other input of the form, so the
 * engine only validates the exact same set of fields as before.
 *
 * @param {jQuery} $form - The address form.
 * @param {string} contextName - 'account' or 'checkout'.
 * @returns {void}
 */
function attachFieldRules($form, contextName) {
    var context = addressConstants.ADDRESS_VALIDATION_CONTEXTS[contextName];
    var contract = addressConstants.ADDRESS_FIELD_CONTRACT;
    var validator = $form.data('validator');

    if (!context || !validator) {
        return;
    }

    Object.keys(contract).forEach(function (fieldName) {
        var selector = context.selectors[fieldName];
        if (!selector) {
            return;
        }

        var rule = contract[fieldName];
        var selectors = [selector];
        if (selector.indexOf('Display') > -1) {
            // controlledPhoneInput renames the visible tel input to
            // '{name}Display' on window.load; at first init the engine may run
            // before that rename, so also resolve the pre-rename selector.
            selectors.push(selector.replace(/Display(?=])/, ''));
        }

        var isRequired = rule.required;

        selectors.forEach(function (fieldSelector) {
            $form.find(fieldSelector).each(function () {
                if (this.type === 'hidden') {
                    return;
                }

                var $field = $(this);
                var rules = $.extend({}, NEUTRALIZED_BUILTIN_RULES);

                if (isRequired) {
                    rules.fgRequired = true;
                }
                if (rule.pattern) {
                    rules.fgPattern = rule.pattern;
                }
                if (rule.min) {
                    rules.minlength = rule.min;
                }
                if (rule.normalize === 'digits') {
                    rules.normalizer = digitsOnly;
                }

                $field.data(FIELD_MARKER, fieldName);
                mirrorValidatorRule(
                    validator,
                    this.name,
                    rules,
                    resolveFieldMessages($field, fieldName)
                );
            });
        });
    });

    $form.find('input, select, textarea').each(function () {
        var $field = $(this);
        if (this.type !== 'hidden' && !$field.data(FIELD_MARKER)) {
            mirrorValidatorRule(
                validator,
                this.name,
                $.extend({}, NEUTRALIZED_BUILTIN_RULES),
                null
            );
        }
    });
}

/**
 * Renders engine results into the existing UI (is-invalid classes and
 * .invalid-feedback text) replicating today's behavior:
 *  - empty required fields keep an already-rendered feedback text;
 *  - pattern errors always overwrite it;
 *  - valid (or empty optional) fields are fully cleared.
 *
 * @param {Object} validator - The jquery-validation instance (this).
 * @returns {void}
 */
function renderAddressErrors(validator) {
    var errorList = validator.errorList;

    /**
     * Finds the rendered message for a specific element in the error list.
     * @param {HTMLElement} element - The field element to look up.
     * @returns {string|null} The message, or null when the element is valid.
     */
    function messageFor(element) {
        for (var i = 0; i < errorList.length; i++) {
            if (errorList[i].element === element) {
                return errorList[i].message;
            }
        }
        return null;
    }

    validator.currentElements.each(function () {
        var $field = $(this);

        if (!$field.data(FIELD_MARKER)) {
            return;
        }

        var message = messageFor(this);
        var $feedback = $field.siblings('.invalid-feedback');
        var isEmpty = $field.val() === undefined || $field.val() === null
            || String($field.val()).trim() === '';
        var isBeingEdited = this === document.activeElement;

        if (message) {
            $field.addClass('is-invalid').parent().addClass('is-invalid');
            if ($feedback.length && !(isEmpty && isBeingEdited && $feedback.text().trim())) {
                $feedback.text(message);
            }
        } else {
            $field.removeClass('is-invalid').parent().removeClass('is-invalid');
            if ($feedback.length) {
                $feedback.text('');
            }
        }
    });
}

/**
 * Initializes the shared client-side validation engine on an address form.
 * Safe to call multiple times: rule resolution re-runs (to cover re-rendered
 * form contents) while the event bridge is bound only once per form element.
 *
 * The engine only reacts to blur/input on managed fields (same triggers as
 * before) and never intercepts form submit: submit gating and server-side
 * validation remain untouched.
 *
 * @param {jQuery} $form - The address form element.
 * @param {string} contextName - 'account' (My Account and sidebar modal, they
 *        share id-based selectors) or 'checkout' (name-suffix selectors).
 * @returns {Object|undefined} The validator instance for the form.
 */
function initAddressForm($form, contextName) {
    if (!$form || !$form.length) {
        return undefined;
    }

    registerEngineMethods();

    var formElement = $form[0];
    var validator = $form.validate({
        onsubmit: false,
        onfocusout: false,
        onkeyup: false,
        onclick: false,
        focusInvalid: false,
        focusCleanup: false,
        ignore: ':hidden',
        showErrors: function () {
            renderAddressErrors(this);
        }
    });

    attachFieldRules($form, contextName);

    if (!$.data(formElement, BRIDGE_MARKER)) {
        $form.on('input.fgAddressValidation focusout.fgAddressValidation change.fgAddressValidation', function (e) {
            var target = e.target;
            if (target && $.data(target, FIELD_MARKER)) {
                if (e.type === 'input' &&
                    (target.type === 'tel' || $.data(target, FIELD_MARKER) === 'postalCode' ||
                        $.data(target, FIELD_MARKER) === 'addressId')) {
                    var $partial = $(target);
                    if ($partial.hasClass('is-invalid')) {
                        $partial.removeClass('is-invalid').parent().removeClass('is-invalid');
                        var $partialFeedback = $partial.siblings('.invalid-feedback');
                        if ($partialFeedback.length) {
                            $partialFeedback.text('');
                        }
                    }
                    return;
                }
                validator.element(target);
            }
        });
        $.data(formElement, BRIDGE_MARKER, true);
    }

    return validator;
}

/**
 * Runs the final (submit-time) validation pass over the managed fields of an
 * initialized address form, marking every invalid field exactly like the old
 * manual loops did. It does not block anything by itself: callers keep their
 * existing gate conditions and the server-side form validation remains the
 * authoritative check.
 *
 * @param {jQuery} $form - The address form element.
 * @param {string} contextName - 'account' or 'checkout'.
 * @returns {boolean} True when all managed fields are currently valid.
 */
function validateAddressForm($form, contextName) {
    var validator = initAddressForm($form, contextName);

    if (!validator) {
        return true;
    }

    return validator.form();
}

/**
 * Re-runs validation over the managed fields of a form after their values have
 * been set programmatically (Google autocomplete / autofill), which changes
 * .val() without emitting input/change/focusout. Each managed field is
 * re-validated individually so a previously shown "required" error clears once
 * the field is filled with a valid value, while an invalid autofilled value
 * still shows the pattern error. It never marks fields that are not currently
 * flagged, so it does not surface premature errors.
 *
 * @param {jQuery} $form - The address form element.
 * @returns {void}
 */
function syncAddressFields($form) {
    if (!$form || !$form.length) {
        return;
    }

    var validator = $form.data('validator');

    if (!validator) {
        return;
    }

    $form.find(':input').each(function () {
        var $field = $(this);
        if ($field.data(FIELD_MARKER) && $field.hasClass('is-invalid')) {
            validator.element(this);
        }
    });
}

/**
 * Validates a single managed field whose value was written by code.
 *  - a filled field is validated, so its error reflects the new value;
 *  - a field emptied by the write loses the error of its previous value and
 *    shows the required message on blur or submit;
 *  - a field that was already empty keeps its current error state.
 *
 * @param {jQuery} $field - The field element.
 * @param {boolean} hadValue - Whether the field had a value before the write.
 * @returns {void}
 */
function validateWrittenField($field, hadValue) {
    var element = $field[0];
    var validator = $field.closest('form').data('validator');

    if (!element || !validator || !$.data(element, FIELD_MARKER)) {
        return;
    }

    var isEmpty = String($field.val() || '').trim() === '';

    if (isEmpty && hadValue) {
        delete validator.invalid[element.name];
        $field.removeClass('is-invalid').parent().removeClass('is-invalid');
        $field.siblings('.invalid-feedback').text('');
        return;
    }

    if (!isEmpty || $field.hasClass('is-invalid')) {
        validator.element(element);
    }
}

/**
 * Interactive blur/input field validation.
 * Handles all three cases: empty required, pattern-fail, valid.
 *
 * @param {jQuery} $field - The field element
 * @param {RegExp} regex - The pattern regex to test against
 * @param {boolean} required - Whether the field is required (default true)
 * @param {Function} [normalize] - Optional value normalizer, applied before the
 *        empty check and the regex test. It must be the SAME function the engine
 *        uses as its rule normalizer (see digitsOnly), so the legacy highlight
 *        path and the engine never diverge on what a value "is".
 */
function validateFieldInteractive($field, regex, required, normalize) {
    if (required === undefined){
        required = true;
    }
    var rawValue = $field.val();
    var value = normalize ? normalize(rawValue) : rawValue;
    var $feedback = $field.siblings('.invalid-feedback');
    var isEmpty = !value || String(value).trim() === '';

    if (isEmpty) {
        if (required) {
            $field.addClass('is-invalid').parent().addClass('is-invalid');
            if ($feedback.length && !$feedback.text().trim()) {
                $feedback.text($field.data('missing-error') || 'Este campo es obligatorio');
            }
        } else {
            $field.removeClass('is-invalid').parent().removeClass('is-invalid');
            if ($feedback.length) {
                $feedback.text('');
            }
        }
    } else if (!regex.test(value)) {
        $field.addClass('is-invalid').parent().addClass('is-invalid');
        if ($feedback.length) {
            $feedback.text($field.data('pattern-mismatch') || 'Formato inválido');
        }
    } else {
        $field.removeClass('is-invalid').parent().removeClass('is-invalid');
        if ($feedback.length) {
            $feedback.text('');
        }
    }
}

/**
 * Validates all pattern-matching fields against their regexes.
 * Uses the provided selector map to locate each field in the form.
 *
 * @param {jQuery} $form - The form element
 * @param {Object} fieldSelectors - Map of field names to CSS selectors
 */
function validatePatternFields($form, fieldSelectors) {
    Object.keys(addressConstants.FIELD_REGEXES).forEach(function (fieldName) {
        if (fieldName === 'externalNumber') return;
        var selector = fieldSelectors[fieldName];
        if (!selector) return;
        var $field = $form.find(selector);
        if (!$field.length) return;
        var rule = addressConstants.ADDRESS_FIELD_CONTRACT[fieldName];
        var normalize = rule && rule.normalize === 'digits' ? digitsOnly : undefined;
        validateFieldInteractive(
            $field,
            addressConstants.FIELD_REGEXES[fieldName],
            addressConstants.OPTIONAL_FIELDS.indexOf(fieldName) < 0,
            normalize
        );
    });
}

/**
 * Validates the external number field.
 * Marks field invalid and shows feedback if pattern fails.
 *
 * @param {jQuery} $form - The form element containing the field
 */
function validateExternalNumber($form) {
    var $externalNumber = $form.find('#externalNumber');
    var value = $externalNumber.val();
    var $feedback = $externalNumber.siblings('.invalid-feedback');

    if (value && !addressConstants.FIELD_REGEXES.externalNumber.test(value)) {
        $externalNumber.addClass('is-invalid').parent().addClass('is-invalid');
        if ($feedback.length) {
            $feedback.text($externalNumber.data('pattern-mismatch') || 'Solo letras, números y . , - /');
        }
    } else if (!value) {
        $externalNumber.addClass('is-invalid').parent().addClass('is-invalid');
        if ($feedback.length && !$feedback.text().trim()) {
            $feedback.text($externalNumber.data('missing-error') || 'Este campo es obligatorio');
        }
    } else {
        $externalNumber.removeClass('is-invalid').parent().removeClass('is-invalid');
    }
}

/**
 * Highlights required fields without values.
 * Adds invalid classes and feedback, then validates external number.
 *
 * @param {jQuery} $form - The form element to check
 */
function highlightInvalidFields($form) {
    addressConstants.REQUIRED_FIELDS.forEach(function (selector) {
        var $field = $form.find(selector);
        if (!$field.val() || $field.val().trim() === '') {
            $field.addClass('is-invalid');
            var $feedback = $field.siblings('.invalid-feedback');
            if ($feedback.length && !$feedback.text().trim()) {
                $feedback.text($field.data('missing-error') || 'Este campo es obligatorio');
            }

            if ($field.is('select')) {
                $field.parent().addClass('is-invalid');
            }
        }
    });

    validatePatternFields($form, addressConstants.ACCOUNT_FIELD_SELECTORS);
    validateExternalNumber($form);
}

/**
 * Highlights empty required fields in the checkout shipping form.
 * Uses name-based selectors to match the checkout form field naming convention.
 * @param {jQuery} $form - The shipping form element
 */
function highlightCheckoutShippingFields($form) {
    Object.keys(addressConstants.CHECKOUT_FIELD_SELECTORS).forEach(function (fieldName) {
        if (addressConstants.OPTIONAL_FIELDS.indexOf(fieldName) > -1) return;
        var selector = addressConstants.CHECKOUT_FIELD_SELECTORS[fieldName];
        var $field = $form.find(selector);
        if (!$field.val() || $field.val().trim() === '') {
            $field.addClass('is-invalid');
            var $feedback = $field.siblings('.invalid-feedback');
            if ($feedback.length && !$feedback.text().trim()) {
                $feedback.text($field.data('missing-error') || 'Este campo es obligatorio');
            }

            if ($field.is('select')) {
                $field.parent().addClass('is-invalid');
            }
        }
    });

    validatePatternFields($form, addressConstants.CHECKOUT_FIELD_SELECTORS);
    var $extNum = $form.find(addressConstants.CHECKOUT_FIELD_SELECTORS.externalNumber);
    var extValue = $extNum.val();
    var $extFeedback = $extNum.siblings('.invalid-feedback');
    if (extValue && !addressConstants.FIELD_REGEXES.externalNumber.test(extValue)) {
        $extNum.addClass('is-invalid').parent().addClass('is-invalid');
        if ($extFeedback.length) {
            $extFeedback.text($extNum.data('pattern-mismatch') || 'Solo letras, números y . , - /');
        }
    }
}

module.exports = {
    highlightInvalidFields: highlightInvalidFields,
    validateExternalNumber: validateExternalNumber,
    highlightCheckoutShippingFields: highlightCheckoutShippingFields,
    validatePatternFields: validatePatternFields,
    validateFieldInteractive: validateFieldInteractive,
    initAddressForm: initAddressForm,
    validateAddressForm: validateAddressForm,
    syncAddressFields: syncAddressFields,
    validateWrittenField: validateWrittenField,
    digitsOnly: digitsOnly
};
